import { useEffect } from 'react';

/**
 * Sets `document.title` while the component is mounted and restores the
 * previous title on unmount. A falsy title leaves the document alone.
 */
export function useDocumentTitle(title: string | null | undefined): void {
  useEffect(() => {
    if (!title) return;
    const previous = document.title;
    document.title = title;
    return () => {
      document.title = previous;
    };
  }, [title]);
}

/**
 * Adds `<meta name="robots" content="noindex">` while mounted (D-10: the SPA
 * can't send a real 404 status, so crawlers get the meta tag instead). An
 * existing robots tag is overridden and put back on unmount; a tag this hook
 * created is removed.
 */
export function useNoIndex(enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    let meta = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
    const created = !meta;
    const previous = meta?.getAttribute('content') ?? null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', 'noindex');
    const tag = meta;
    return () => {
      if (created) tag.remove();
      else if (previous !== null) tag.setAttribute('content', previous);
    };
  }, [enabled]);
}

/**
 * Sets `<meta {attr}="{key}" content>` while mounted; a tag that already
 * exists (index.html defaults) is overridden and put back on unmount, a tag
 * this hook created is removed. A falsy content leaves the document alone.
 */
function useHeadMeta(attr: 'name' | 'property', key: string, content: string | null | undefined): void {
  useEffect(() => {
    if (!content) return;
    let meta = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
    const created = !meta;
    const previous = meta?.getAttribute('content') ?? null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute(attr, key);
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
    const tag = meta;
    return () => {
      if (created) tag.remove();
      else if (previous !== null) tag.setAttribute('content', previous);
    };
  }, [attr, key, content]);
}

/** `<link rel="canonical">` while mounted (same override/restore rules). */
function useCanonical(href: string | null | undefined): void {
  useEffect(() => {
    if (!href) return;
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const created = !link;
    const previous = link?.getAttribute('href') ?? null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', href);
    const tag = link;
    return () => {
      if (created) tag.remove();
      else if (previous !== null) tag.setAttribute('href', previous);
    };
  }, [href]);
}

export const BRAND = 'Salunas';

/** "Your bookings" → "Your bookings · Salunas"; titles that already carry the brand are kept. */
export function withBrand(title: string): string {
  return title.includes(BRAND) ? title : `${title} · ${BRAND}`;
}

/** Collapses whitespace and cuts at a word boundary to ≤ `max` chars (meta descriptions). */
export function clampDescription(text: string, max = 160): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max - 1);
  const space = cut.lastIndexOf(' ');
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[\s,.;:–—-]+$/, '')}…`;
}

/**
 * The canonical the server rendered into this document, if any. The Salunas
 * shell renderer (backend /api/v1/marketplace/render/*) decides the canonical
 * host from the salon's country (a BE salon is canonical on salunas.be even
 * when read on salunas.com); the hydrated SPA must not override that with the
 * request origin. Captured once at module load, before React mounts, and only
 * honoured while the pathname is still the one the server rendered.
 */
const serverCanonical: { path: string; href: string } | null = (() => {
  if (typeof document === 'undefined') return null;
  const href = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.getAttribute('href');
  return href ? { path: window.location.pathname, href } : null;
})();

/** Absolute canonical for a path: the server's choice for the initial document, else this host. */
export function canonicalUrl(pathname: string): string {
  if (typeof window === 'undefined') return pathname;
  if (serverCanonical && serverCanonical.path === pathname) return serverCanonical.href;
  return `${window.location.origin}${pathname}`;
}

export interface DocumentMeta {
  /** Page title; " · Salunas" is appended unless the brand is already in it. */
  title?: string | null;
  description?: string | null;
  /** Booking, account and auth pages: `<meta name="robots" content="noindex">`. */
  noindex?: boolean;
  /** Absolute canonical URL (also used as og:url). */
  canonical?: string | null;
  /** Absolute share image (og:image / twitter:image). */
  image?: string | null;
  /** og:type; index.html's default is "website". */
  ogType?: string | null;
}

/**
 * Per-route document meta (M7, client-side only — crawlers that don't run JS
 * still see index.html's defaults; SSR/prerender is a follow-up). Sets the
 * title, description, robots, canonical and the Open Graph / Twitter tags,
 * restoring index.html's values on unmount.
 */
export function useDocumentMeta(meta: DocumentMeta): void {
  const title = meta.title ? withBrand(meta.title) : null;
  useDocumentTitle(title);
  useNoIndex(!!meta.noindex);
  useHeadMeta('name', 'description', meta.description);
  useHeadMeta('property', 'og:title', title);
  useHeadMeta('property', 'og:description', meta.description);
  useHeadMeta('property', 'og:image', meta.image);
  useHeadMeta('property', 'og:url', meta.canonical);
  useHeadMeta('property', 'og:type', meta.ogType);
  useHeadMeta('name', 'twitter:title', title);
  useHeadMeta('name', 'twitter:description', meta.description);
  useHeadMeta('name', 'twitter:image', meta.image);
  useCanonical(meta.canonical);
}
